import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ConditionalRendering } from '@components/common/conditional-rendering';

interface IMultiSelectDropdownProps {
    labelId: string,
    optionsList: string[],
    selectedOptions: string[],
    handleSelect: (option: string) => void,
    handleTagRemoval: (option: string) => void,
}   

const MultiSelectDropdown = (props: IMultiSelectDropdownProps) => {
    const { labelId, optionsList, selectedOptions, handleSelect, handleTagRemoval } = props;
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const toggleDropdown = () => setIsOpen(!isOpen);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="multi-select-wrapper" 
            ref={dropdownRef} role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            data-testid="multi-select-wrapper"
        >
            <label htmlFor="multi-select-input" data-testid="multi-select-label"><FormattedMessage id={labelId}/></label>
            <div className="multi-select-box" data-testid="multi-select-box">
                <div className='multi-select-tags' 
                    id="multi-select-input"
                    onClick={toggleDropdown}
                    role="button"
                    aria-label="Selected options"
                    data-testid="multi-select-tags"
                > 
                    { selectedOptions.map((item) => (
                        <span className="tag" key={item} data-testid={`multi-select-tag-${item}`}>
                            {item}
                            <button onClick={(e) => { e.stopPropagation(); handleTagRemoval(item); }}
                                aria-label={`Remove ${item}`}
                                data-testid={`remove-tag-${item}`}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </span>))
                    }
                    <span className="dropdown-arrow" data-testid="dropdown-arrow">
                        <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
                    </span>
                </div>
                <ConditionalRendering isTrue={isOpen}>
                    <div className="multi-select-dropdown" aria-label="Mult select status options" data-testid="multi-select-dropdown">
                        {optionsList.map((option) => (
                            <div
                                key={option}
                                className={`dropdown-option ${selectedOptions.includes(option) ? 'selected' : ''}`}
                                onClick={() => handleSelect(option)}
                                role="option"
                                aria-selected={selectedOptions.includes(option)}
                                data-testid={`dropdown-option-${option}`}
                            >
                                <span className="checkbox-box" data-testid={`checkbox-box-${option}`}>
                                    <ConditionalRendering isTrue={selectedOptions.includes(option)}>
                                        <FontAwesomeIcon icon={faCheck} className="check-icon" aria-hidden="true" />
                                    </ConditionalRendering>
                                </span>
                                {option}
                            </div>
                        ))}
                    </div>
                </ConditionalRendering>
            </div>
        </div>
    );
};

export default MultiSelectDropdown;